import React, { useEffect, useRef, useState } from "react";
import { venuePhotos } from "../data/venuePhotos";
import Icon from "../components/Icon";
import "../css/Gallery.css";

export default function Gallery() {
  const [activeIndex, setActiveIndex] = useState(null);
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);
  const isOpen = activeIndex !== null;
  const activePhoto = isOpen ? venuePhotos[activeIndex] : null;
  const move = (direction) =>
    setActiveIndex(
      (index) => (index + direction + venuePhotos.length) % venuePhotos.length,
    );
  useEffect(() => {
    if (!isOpen) return undefined;
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
    dialog.querySelector("button")?.focus();
    return () => {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
      document.body.style.overflow = previousOverflow;
      triggerRef.current?.focus();
    };
  }, [isOpen]);
  return (
    <div className="gallery">
      <div className="section-heading">
        <p className="eyebrow">A PEEK INSIDE THE KINGDOM</p>
        <h2>See where the fun happens.</h2>
        <p>
          Pastel castles, colorful play, and room for little imaginations. These
          are our real spaces and happy moments at Kiddie Kingdom.
        </p>
      </div>
      <div className="venue-gallery-grid">
        {venuePhotos.map((photo, index) => (
          <button
            className="venue-photo-card"
            key={photo.src}
            type="button"
            aria-label={`Open photo: ${photo.caption}`}
            onClick={(event) => {
              triggerRef.current = event.currentTarget;
              setActiveIndex(index);
            }}
          >
            <img
              src={photo.small}
              srcSet={`${photo.small} 480w, ${photo.src} 1280w`}
              sizes="(min-width: 1000px) 33vw, (min-width: 600px) 50vw, 100vw"
              alt={photo.alt}
              width={photo.width}
              height={photo.height}
              loading="lazy"
              decoding="async"
            />
            <span className="photo-card-caption">
              {photo.caption}
              <Icon name="external" />
            </span>
          </button>
        ))}
      </div>
      <p className="gallery-help">Tap a photo for a closer look.</p>
      <dialog
        ref={dialogRef}
        className="gallery-dialog"
        aria-labelledby="gallery-dialog-title"
        onCancel={(event) => {
          event.preventDefault();
          setActiveIndex(null);
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) setActiveIndex(null);
        }}
        onKeyDown={(event) => {
          if (event.key === "Tab") {
            const controls = event.currentTarget.querySelectorAll("button");
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }
          if (event.key === "ArrowRight") {
            event.preventDefault();
            move(1);
          }
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            move(-1);
          }
        }}
      >
        <div className="gallery-dialog-content">
          <div className="gallery-dialog-header">
            <h2 id="gallery-dialog-title">The playground, up close</h2>
            <button
              className="icon-button"
              aria-label="Close photo viewer"
              type="button"
              onClick={() => setActiveIndex(null)}
            >
              <Icon name="close" />
            </button>
          </div>
          {activePhoto && (
            <img
              className="gallery-full-photo"
              src={activePhoto.src}
              alt={activePhoto.alt}
              width={activePhoto.width}
              height={activePhoto.height}
            />
          )}
          <div className="gallery-dialog-controls">
            <button
              className="icon-button"
              aria-label="Previous photo"
              type="button"
              onClick={() => move(-1)}
            >
              <Icon name="previous" />
            </button>
            <p aria-live="polite">
              {isOpen &&
                `${activeIndex + 1} / ${venuePhotos.length} · ${activePhoto.caption}`}
            </p>
            <button
              className="icon-button"
              aria-label="Next photo"
              type="button"
              onClick={() => move(1)}
            >
              <Icon name="next" />
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}
